import { Dropdown as FabricDropdown, IDropdownOption, IDropdownProps, ISelectableOption } from "@fluentui/react";
import React, { useEffect, useState } from "react";

export type DropdownMultiselectOption = IDropdownOption;

export interface DropdownMultiselectProps
    extends Pick<IDropdownProps, "label" | "placeholder" | "selectedKeys" | "options" | "onRenderTitle" | "disabled"> {
    width?: number;
    testId?: string;
    onChange?(keys?: (string | number)[]): void;
}

const optionAllKey = "all";

export const DropdownMultiselect: React.FunctionComponent<DropdownMultiselectProps> = (
    props: DropdownMultiselectProps
) => {
    const [selectedKeys, setSelectedKeys] = useState<(number | string)[]>([]);
    const [options, setOptions] = useState<IDropdownOption[]>([]);
    const startingKeys = props.selectedKeys ? props.selectedKeys : [];

    useEffect(() => {
        if (!!props.selectedKeys && props.selectedKeys.length) {
            const isAllSelected = checkIfSelectedAllKeys(props.options, props.selectedKeys);

            setSelectedKeys(isAllSelected ? [optionAllKey, ...props.selectedKeys] : props.selectedKeys);
        }
    }, [props.selectedKeys, props.options]);

    useEffect(() => {
        setOptions([{ key: optionAllKey, text: "All" }, ...props.options]);
    }, [props.options]);

    const checkIfSelectedAllKeys = (opts: IDropdownOption[], keys: (number | string)[]) => {
        if (!opts.length) return false;

        return opts.every((o: IDropdownOption) => keys.includes(o.key));
    };

    const onChange = (_event: React.FormEvent<HTMLDivElement>, option?: DropdownMultiselectOption): void => {
        if (option) {
            setSelectedKeys((prevSelectedKeys) => {
                let newSelectedKeys;
                if (option.key === optionAllKey) {
                    newSelectedKeys = option.selected ? options.map((o) => o.key) : [];
                } else {
                    if (option.selected) {
                        newSelectedKeys = [...prevSelectedKeys, option.key];
                        const isAllSelected = checkIfSelectedAllKeys(props.options, newSelectedKeys);
                        if (isAllSelected) newSelectedKeys = options.map((o) => o.key);
                    } else {
                        newSelectedKeys = prevSelectedKeys.filter((key) => key !== option.key && key !== optionAllKey);
                    }
                }
                return newSelectedKeys;
            });
        }
    };

    const onDismiss = () => {
        if (areKeyArraysChanged(startingKeys, selectedKeys) && props.onChange) {
            props.onChange(selectedKeys.filter((s) => s !== optionAllKey));
        }
    };

    const renderOption = (option?: ISelectableOption) => {
        if (!option) return <></>;

        const dataTestKey = option.key ? { "data-testkey": option.key } : {};

        return <div {...dataTestKey}>{option.text}</div>;
    };

    return (
        <FabricDropdown
            {...props}
            label={props.label}
            selectedKeys={selectedKeys as string[] | number[]}
            onChange={onChange}
            onDismiss={onDismiss}
            placeholder={props.placeholder}
            options={options}
            multiSelect={true}
            data-testId={props.testId}
            styles={{ dropdown: { width: props.width || "100%" } }}
            onRenderOption={renderOption}
            disabled={props.disabled}
        />
    );
};

const areKeyArraysChanged = (startingKeys: (number | string)[], selectedKeys: (number | string)[]): boolean => {
    if (startingKeys.length !== selectedKeys.length) {
        return true;
    }

    const arr1 = startingKeys.sort();
    const arr2 = selectedKeys.sort();

    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
            return true;
        }
    }

    return false;
};
